import Analytics, {
  InteractionInputEvent,
  MilestoneInputEvent,
  OptimisationInputEvent,
} from '@ecs-uk/web-common-analytics';

// a user has attempted an invalid product change and has been redirected away from the switch page
export const trackSwitchRejected = () => {
  Analytics.publish(
    MilestoneInputEvent.fromObject({
      product_movement_switch_rejected: {
        interaction_status: false,
      },
    })
  );
};

export const trackSwitchPaymentMethodSelected = paymentMethodType => {
  Analytics.publish(
    InteractionInputEvent.fromObject({
      component: 'prodmove_switch_form_payment_method_select',
      action: 'input',
      status: true,
      data: {
        payment_method: paymentMethodType,
      },
    })
  );
};

export const trackSwitchPaymentCardNumValid = () => {
  Analytics.publish(
    InteractionInputEvent.fromObject({
      component: 'prodmove_switch_form_card_number',
      action: 'input',
      status: true,
    })
  );
};

export const trackSwitchPaymentFirstNameValid = () => {
  Analytics.publish(
    InteractionInputEvent.fromObject({
      component: 'prodmove_switch_form_first_name',
      action: 'input',
      status: true,
    })
  );
};

export const trackSwitchPaymentLastNameValid = () => {
  Analytics.publish(
    InteractionInputEvent.fromObject({
      component: 'prodmove_switch_form_last_name',
      action: 'input',
      status: true,
    })
  );
};

export const trackSwitchPaymentCardExpiryMonthValid = () => {
  Analytics.publish(
    InteractionInputEvent.fromObject({
      component: 'prodmove_switch_form_card_month_expiry',
      action: 'input',
      status: true,
    })
  );
};

export const trackSwitchPaymentCardExpiryYearValid = () => {
  Analytics.publish(
    InteractionInputEvent.fromObject({
      component: 'prodmove_switch_form_card_year_expiry',
      action: 'input',
      status: true,
    })
  );
};

export const trackSwitchPaymentCvvValid = () => {
  Analytics.publish(
    InteractionInputEvent.fromObject({
      component: 'prodmove_switch_form_cvv',
      action: 'input',
      status: true,
    })
  );
};

export const trackSwitchFormAttempt = (successful, paymentMethodType) => {
  Analytics.publish(
    InteractionInputEvent.fromObject({
      component: 'prodmove_confirm_purchase_button',
      action: 'click',
      status: successful,
      data: {
        payment_method: paymentMethodType,
      },
    })
  );
};

// Data 4.0 event to track the result of attempting to tokenize the payment method
export const trackPaymentMethodTokenizationResult = (successful, paymentMethod) => {
  const paymentMethodMap = { new_gpay: 'gpay', new_apple_pay: 'apple_pay' };
  const normalisedPaymentMethod = paymentMethodMap[paymentMethod] ?? paymentMethod;
  Analytics.trackSelfDescribingEvent({
    schema: 'iglu:uk.co.experian.subscriptions/payment_method_tokenization_attempted/jsonschema/1-0-0',
    data: {
      successful,
      payment_method: normalisedPaymentMethod,
      journey: 'product_movement',
    },
  });
};

// Data 4.0 event for when the output token is received from 3DS challenge
export const track3dsOutputTokenReceived = () => {
  Analytics.trackSelfDescribingEvent({
    schema: 'iglu:uk.co.experian.subscriptions/three_d_secure_output_token_received/jsonschema/1-0-0',
    data: {
      journey: 'product_movement',
    },
  });
};

// Data 4.0 event for when there is a failure in the 3DS challenge
export const track3dsChallengeFailure = (errorCode, errorMessage) => {
  Analytics.trackSelfDescribingEvent({
    schema: 'iglu:uk.co.experian.subscriptions/three_d_secure_failed/jsonschema/1-0-0',
    data: {
      journey: 'product_movement',
      error_code: errorCode ?? 'unknown',
      error_message: errorMessage ?? 'unknown',
    },
  });
};

export const trackSwitchInitiatePurchase = successful => {
  Analytics.publish(
    MilestoneInputEvent.fromObject({
      product_movement_initiate_purchase: {
        interaction_status: successful,
      },
    })
  );
};

export const trackSwitchAuthReady = () => {
  Analytics.publish(
    MilestoneInputEvent.fromObject({
      product_movement_challenge_page_load: {
        interaction_status: true,
      },
    })
  );
};

export const trackSwitchAuthSuccess = () => {
  Analytics.publish(
    MilestoneInputEvent.fromObject({
      product_movement_auth_success: {
        interaction_status: true,
      },
    })
  );
};

export const trackSwitchAuthFailure = errorCode => {
  Analytics.publish(
    MilestoneInputEvent.fromObject({
      product_movement_auth_error: {
        error_type: errorCode ?? 'unknown',
      },
    })
  );
};

export const trackSwitchAuthorizePurchase = successful => {
  Analytics.publish(
    MilestoneInputEvent.fromObject({
      product_movement_authorize_purchase: {
        interaction_status: successful,
      },
    })
  );
};
export const trackSwitchFulfillmentStarted = () => {
  Analytics.publish(
    MilestoneInputEvent.fromObject({
      product_movement_fulfillment_started: {
        interaction_status: true,
      },
    })
  );
};

export const trackSwitchFulfillmentAttempt = successful => {
  Analytics.publish(
    MilestoneInputEvent.fromObject({
      product_movement_fulfillment: {
        interaction_status: successful,
      },
    })
  );
};

export const trackSwitchEntitlementsPollRetry = attempts => {
  Analytics.publish(
    MilestoneInputEvent.fromObject({
      product_movement_entitlements_poll_retry: {
        attempts,
      },
    })
  );
};

export const trackSwitchEntitlementsPollTimeout = (attempts, elapsedMs) => {
  Analytics.publish(
    MilestoneInputEvent.fromObject({
      product_movement_entitlements_poll_timeout: {
        attempts,
        elapsed_ms: elapsedMs,
      },
    })
  );
};

export const trackSwitchGoBackClick = () => {
  Analytics.publish(
    InteractionInputEvent.fromObject({
      component: 'prod_move_upsell_go_back_cta',
      action: 'click',
      status: 'success',
    })
  );
};

export const trackPaymentAndBillingBannerClick = () => {
  Analytics.publish(
    InteractionInputEvent.fromObject({
      component: 'payment_and_billing_banner',
      action: 'click',
      status: 'success',
    })
  );
};

export const trackPaymentMethodChangeStarted = (paymentOptionsAvailable, currentPaymentMethod) => {
  Analytics.publishOnce(
    InteractionInputEvent.fromObject({
      component: 'change_payment_method',
      action: 'click',
      status: 'success',
      data: {
        payment_options_available: paymentOptionsAvailable,
        current_payment_method: currentPaymentMethod,
      },
    })
  );
};

export const trackPaymentMethodChangeCancel = () => {
  Analytics.publish(
    InteractionInputEvent.fromObject({
      component: 'cancel_payment_update_button',
      action: 'click',
      status: 'success',
    })
  );
};

export const trackPaymentMethodChangeFinished = ({ updateStatus, attemptedUpdateMethod }) => {
  Analytics.publish(
    MilestoneInputEvent.fromObject({
      update_subscription_payment_method: {
        status: updateStatus,
        data: {
          attempted_method: attemptedUpdateMethod,
        },
      },
    })
  );
};

export const trackPaymentMethodChangeError = error => {
  const onErrorECD = MilestoneInputEvent.fromObject({
    error: {
      component: 'payment_update',
      application: 'product_movement',
      error_state: error,
    },
  });
  Analytics.publish(onErrorECD);
};

export const trackApplePayFeatureAvailability = treatment => {
  Analytics.publishOnce(
    OptimisationInputEvent.fromObject({
      testName: 'applepay_blanket_feature_toggle',
      testArea: 'Product Movement',
      testVariant: treatment,
    })
  );
};

export const trackApplePayWebRollout = treatment => {
  Analytics.publishOnce(
    OptimisationInputEvent.fromObject({
      testName: 'applepay_web_payment_method',
      testArea: 'Product Movement',
      testVariant: treatment,
    })
  );
};

export const trackCardOnFileSplit = hasSavedCard => {
  Analytics.publishOnce(
    OptimisationInputEvent.fromObject({
      testName: 'card_on_file',
      testArea: 'Product Movement',
      testVariant: hasSavedCard ? 'B' : 'A',
    })
  );
};

export const trackGooglePayFeatureAvailability = treatment => {
  Analytics.publishOnce(
    OptimisationInputEvent.fromObject({
      testName: 'gpay_blanket_feature_toggle',
      testArea: 'Product Movement',
      testVariant: treatment,
    })
  );
};

export const trackAvatarProfileMVPExperience = treatment => {
  Analytics.publishOnce(
    OptimisationInputEvent.fromObject({
      testName: 'avatar_profile_mvp_experience',
      testArea: 'Product Movement',
      testVariant: treatment,
    })
  );
};

export const trackBillingProviderIdentifier = provider => {
  Analytics.publishOnce(
    OptimisationInputEvent.fromObject({
      testName: 'billing_provider_migration',
      testArea: 'Product Movement',
      testVariant: provider,
    })
  );
};

export const onSwitchPageLoadECD = ({
  currentSubscriptionFamily,
  targetSubscriptionFamily,
  userEligibleForTrial,
  userCurrentlyInFreeTrial,
  subscriptionPendingCancellation,
  remainingTrialDays,
}) => {
  const productMovementIntentEvent = MilestoneInputEvent.fromObject({
    product_movement_intent: {
      subscription_from: currentSubscriptionFamily,
      subscription_to: targetSubscriptionFamily || 'unknown',
      is_eligible_for_trial: userEligibleForTrial ?? 'not_set',
      family: currentSubscriptionFamily,
      productSubscriptionLookingToMoveTo: targetSubscriptionFamily,
      isWithinFreeTrial: userCurrentlyInFreeTrial,
      isPendingCancellation: subscriptionPendingCancellation,
      daysLeftOfTrialInCurrentProductSubscription: remainingTrialDays,
    },
  });

  Analytics.publishOnce(productMovementIntentEvent);
};

export const submitInteractionECD = (action, component, status) => {
  Analytics.publishOnce(
    InteractionInputEvent.fromObject({
      component,
      action,
      status,
    })
  );
};

export const submitSwitchPageClickECD = componentName => {
  submitInteractionECD('click', componentName, 'success');
};

export const trackSwitchToFreeButtonClick = variant => {
  Analytics.publishOnce(
    OptimisationInputEvent.fromObject({
      testName: 'UKECO-1201 ID retention test',
      testArea: 'Product Movement',
      testVariant: variant,
    })
  );
};

export const trackCoolingOffCancelClick = (subscriptionFamily, cancelType, step) => {
  Analytics.publish(
    InteractionInputEvent.fromObject({
      component: `prodmove_switch_cancel_${subscriptionFamily}_${cancelType}_${step}`,
      action: 'click',
      status: 'success',
    })
  );
};

export const trackCoolingOffConfirmCancelClick = (subscriptionFamily, cancelType) => {
  Analytics.publish(
    InteractionInputEvent.fromObject({
      component: `prodmove_switch_confirm_cancel_${subscriptionFamily}_${cancelType}`,
      action: 'click',
      status: 'success',
    })
  );
};

export const trackKeepSubscriptionClick = (subscriptionFamily, step) => {
  Analytics.publish(
    InteractionInputEvent.fromObject({
      component: `prodmove_switch_keep_${subscriptionFamily}_from_${step}`,
      action: 'click',
      status: 'success',
    })
  );
};
